import { LOCALES } from 'constants/dateTime';
import { CURRENCY_TYPE } from 'constants/enums';
import { convertExponentialToDecimal } from 'helpers/assetUtils';
import { formatFiat } from 'helpers/currency';
import {
  addZeroesUntilCorrectPrecision,
  convertFromUSDtoINR,
  cropAfterDecimals,
} from 'helpers/utils';
import { SpotTickerData } from 'stores/spotIndia/spotIndia.types';

import { SYMBOL_CONFIG } from './constants';
import { SpotProduct } from './types';

export const formatBalance = (
  balance: number,
  precision: number,
  currencyType: CURRENCY_TYPE,
  locale?: (typeof LOCALES)[keyof typeof LOCALES]
): string => {
  return formatFiat(
    currencyType,
    cropAfterDecimals(
      addZeroesUntilCorrectPrecision(convertExponentialToDecimal(balance), precision),
      precision
    ),
    locale
  );
};

export const calculateInrValue = (
  balance: number,
  assetSymbol: string,
  inrConversionRate: number
): number => {
  return assetSymbol === 'INR'
    ? balance
    : Number(convertFromUSDtoINR(balance, inrConversionRate));
};

export const calculateAssetValueInINR = (
  balance: number,
  assetSymbol: string,
  spotPrices: Record<string, number | undefined>,
  spotProduct?: SpotProduct,
  spotIndiaIndicis?: Record<string, string>
): number => {
  if (assetSymbol === 'INR' && balance !== undefined) {
    return balance;
  }

  if (spotIndiaIndicis && spotProduct?.id) {
    const priceKey = spotIndiaIndicis[spotProduct.id];
    const spotPrice = priceKey ? spotPrices[priceKey] : undefined;
    if (spotPrice !== undefined) {
      return balance * spotPrice;
    }
  }

  return 0;
};

export const getAssetSpotPrice = (
  spotPrices: Record<string, number | undefined>,
  spotProduct?: SpotProduct | null,
  spotIndiaIndicis?: Record<string, string>
): number | null => {
  if (spotIndiaIndicis && spotProduct?.id) {
    const priceKey = spotIndiaIndicis[spotProduct.id];
    const spotPrice = priceKey ? spotPrices[priceKey] : undefined;
    if (spotPrice !== undefined) {
      return spotPrice;
    }
  }

  return null;
};

export const formatBalanceInCurrency = (
  balance: string | number,
  precision: number,
  inrConversionRate: number,
  currencyType: CURRENCY_TYPE
): string => {
  const numericBalance = Number(balance);
  const balanceValue =
    currencyType === CURRENCY_TYPE.INR
      ? Number(convertFromUSDtoINR(numericBalance, inrConversionRate))
      : numericBalance;

  return formatBalance(balanceValue, precision, currencyType, LOCALES.INDIA);
};

export const calculate24hChange = (
  symbol: string,
  spotTickerData: SpotTickerData
): number | null => {
  const tickerSymbol = `${symbol}${SYMBOL_CONFIG.TICKER_SUFFIX}`;
  const tickerInfo = spotTickerData[tickerSymbol];

  if (!tickerInfo || !tickerInfo.open || !tickerInfo.close) {
    return null;
  }

  const changePercent = ((tickerInfo.close - tickerInfo.open) / tickerInfo.open) * 100;
  return changePercent;
};

export const format24hChange = (changePercent: number | null): string | null => {
  if (changePercent === null) return null;
  return `${changePercent?.toFixed(2)}%`;
};

export const isOnSpotRouteNative = () => {
  return typeof window !== 'undefined' && window?.location?.href?.includes('spot-market');
};

export const isOnLoaderRoute = () => {
  return typeof window !== 'undefined' && window?.location?.href?.includes('loader');
};
